import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toViewerZone, viewerTz } from '../utils/timeZone';
import axios from 'axios';
import { CalendarCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/appointments/booked-for-me-notices`;
const POLL_MS = 60000;
const otherDialogOpen = () => !!document.querySelector('[role="dialog"], [role="alertdialog"]');
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '');
const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return new Date(2000, 0, 1, h, m).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

// Pop-up shown once when another staff member books an appointment for you.
export const BookedForYouNotice = () => {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const shown = useRef(new Set());

  const check = useCallback(() => {
    if (!user) return;
    axios.get(API).then((r) => setQueue((q) => [...q, ...r.data.notices.filter((n) => !shown.current.has(n.id) && !q.some((x) => x.id === n.id))])).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    const first = setTimeout(check, 4000);
    const poll = setInterval(check, POLL_MS);
    return () => { clearTimeout(first); clearInterval(poll); };
  }, [user, check]);

  useEffect(() => {
    if (current || !queue.length) return undefined;
    const tryShow = () => { if (!otherDialogOpen()) { shown.current.add(queue[0].id); setCurrent(queue[0]); setQueue((q) => q.slice(1)); } };
    tryShow();
    const t = setInterval(tryShow, 2000);
    return () => clearInterval(t);
  }, [queue, current]);

  if (!current) return null;
  const cv = toViewerZone(current, viewerTz(user));
  const close = () => { axios.post(`${API}/${current.id}/seen`).catch(() => {}); setCurrent(null); };
  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md" data-testid="booked-for-you-notice">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarCheck className="h-5 w-5 text-primary" />An appointment was booked for you</DialogTitle>
          <DialogDescription>{current.booked_by_name} booked an appointment and assigned it to you.</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border p-3 space-y-1 text-sm" data-testid="booked-for-you-details">
          <div className="font-medium">{current.title}</div>
          {current.participant_name && <div>Participant: {current.participant_name}</div>}
          <div>{fmtDate(cv.date)}{cv.start_time ? ` at ${fmtTime(cv.start_time)}${cv.tz_abbr ? ` ${cv.tz_abbr}` : ''}` : ''}</div>
          {current.occurrences > 1 && <div className="text-muted-foreground">Repeats: {current.occurrences} appointments in this series</div>}
        </div>
        <DialogFooter><Button onClick={close} data-testid="booked-for-you-ok-btn">Got it</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
